import React from 'react';
import CopyToClipboard from '@amzn/awsui-components-console/copy-to-clipboard';

import { useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * "Share search" button: copies a shareable link for the CURRENT selection.
 *
 * The link is built from `shareParams` (the page's live draft encoded via its
 * URL codec), not `window.location`, so it matches the dropdowns exactly.
 * Reusable by any page with a URL codec; disabled when `disabled` is set or
 * there is nothing to share. Reads `window.location` only for origin/path.
 */
export interface ShareSearchButtonProps {
  /**
   * Query params for the current selection, built by the page from its live
   * draft via its URL codec — or null when there is nothing to share.
   */
  readonly shareParams: URLSearchParams | null;
  /** Page-level enablement gate ORed with the "nothing to share" check. */
  readonly disabled?: boolean;
  /** Tooltip shown while disabled. Defaults to a localized explanation. */
  readonly disabledReason?: string;
  /** Button label. Defaults to the localized "Share search". */
  readonly copyButtonText?: string;
  /** Accessible name, in case several copy buttons share a screen. */
  readonly copyButtonAriaLabel?: string;
}

function hasEntries(params: URLSearchParams | null): params is URLSearchParams {
  return params != null && Array.from(params).length > 0;
}

const ShareSearchButton: React.FC<ShareSearchButtonProps> = ({
  shareParams,
  disabled,
  disabledReason,
  copyButtonText,
  copyButtonAriaLabel,
}) => {
  const { t } = useTranslation();
  const shareable = hasEntries(shareParams);
  const isDisabled = (disabled ?? false) || !shareable;

  const { origin, pathname } =
    typeof window !== 'undefined'
      ? window.location
      : { origin: '', pathname: '' };
  // Absolute URL carrying the live selection, so it matches the dropdowns.
  const textToCopy = `${origin}${pathname}${
    shareable ? `?${shareParams.toString()}` : ''
  }`;

  return (
    <CopyToClipboard
      variant="button"
      copyButtonText={copyButtonText ?? t('share-search-button')}
      copyButtonAriaLabel={copyButtonAriaLabel ?? t('share-search-aria')}
      textToCopy={textToCopy}
      copySuccessText={t('share-search-success')}
      copyErrorText={t('share-search-error')}
      disabled={isDisabled}
      disabledReason={disabledReason ?? t('share-search-disabled-reason')}
    />
  );
};

export default ShareSearchButton;
